import type { AnalyticsConfig } from "./config";
import { maskContentAttribute } from "./mask-attributes";
import { redactServerDetail } from "./redact-server-detail";
import { stripEventQueryStrings } from "./strip-query-strings";

/** Pinned so a library upgrade cannot quietly change what un-set means. */
const CONFIG_DEFAULTS = "2026-06-25";

/** Drafts carry unpublished invention text, so the region is pinned here. */
const EU_INGESTION_HOST = "https://eu.i.posthog.com";

export type PostHog = typeof import("posthog-js").default;

/**
 * The one SDK instance, shared by analytics and support: the SDK is a global,
 * so a second `init` would reconfigure the first rather than make a second.
 * `load` resolves `null` when the SDK never arrives, which leaves PostHog off
 * for the visit; that is a content blocker's decision to make.
 */
export interface PostHogSdk {
  load: () => Promise<PostHog | null>;
}

/**
 * Use the SDK once it has arrived, or never when it does not arrive. Calls run in the
 * order they were made, so an event captured before the SDK arrives still reaches it
 * behind the identify that preceded it.
 */
export function whenReady(
  sdk: PostHogSdk,
  use: (posthog: PostHog) => void,
): void {
  void sdk.load().then((posthog) => {
    if (posthog) use(posthog);
  });
}

/**
 * Uncaught errors and unhandled rejections are the failures no one files a
 * ticket for. Console errors stay out: their arguments carry whatever object
 * failed, and on this app that can be draft content.
 */
const REPORT_UNCAUGHT_FAILURES = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
} as const;

/**
 * A replay shows the shape of what someone did, never what they wrote or read.
 * Attributes stay unmasked because class, style and src are what make a replay
 * legible as a page at all.
 */
const MASK_EVERYTHING = {
  maskTextSelector: "*",
  maskAllInputs: true,
  maskAttributeFn: maskContentAttribute,
  // Every image is a figure of the invention or the RWS logo. Blocking the
  // element type means a new figure component cannot forget to opt out.
  blockSelector: "img",
  recordBody: false,
  recordHeaders: false,
} as const;

/**
 * A dynamic import, so the SDK is its own chunk that nothing on screen waits
 * for and a build with no project key never fetches. Autocapture, automatic
 * pageviews and console capture stay off: each carries page content.
 */
async function startPostHog(config: AnalyticsConfig): Promise<PostHog> {
  const { default: posthog } = await import("posthog-js");

  posthog.init(config.projectKey, {
    api_host: EU_INGESTION_HOST,
    defaults: CONFIG_DEFAULTS,
    // What was clicked, on a draft page, is the draft.
    autocapture: false,
    // Single-page router: views are reported by hand where the route is known.
    capture_pageview: false,
    capture_pageleave: false,
    // Covers what the SDK builds itself: URLs read from window.location, and
    // the message of an API failure nobody caught. The stage is stamped here
    // rather than registered, because a reset clears what was registered.
    before_send: (event) =>
      event &&
      withEnvironment(
        redactServerDetail(stripEventQueryStrings(event)),
        config.environment,
      ),
    disable_session_recording: !config.sessionReplay,
    session_recording: MASK_EVERYTHING,
    // Forced off rather than left to the project, which defaults to on: replay
    // console output is unmasked, and caught errors here can quote the draft.
    enable_recording_console_log: false,
    capture_exceptions: REPORT_UNCAUGHT_FAILURES,
    disable_surveys_automatic_display: true, // Only the card, with its warning line.
    // A profile before sign-in would duplicate the person who signs in next.
    person_profiles: "identified_only",
  });

  return posthog;
}

function withEnvironment<T extends { properties: Record<string, unknown> }>(
  event: T,
  environment: string,
): T {
  return {
    ...event,
    properties: { ...event.properties, praxis_environment: environment },
  };
}

export function createPostHogSdk(config: AnalyticsConfig): PostHogSdk {
  // Started by the first call, so the chunk stays off the first-paint path.
  let loading: Promise<PostHog | null> | null = null;

  return {
    load() {
      loading ??= startPostHog(config).catch(() => null);
      return loading;
    },
  };
}
